import { computed } from 'vue'
import { useUserAuthStore } from '@/stores/user-auth'
import { useSearchStore } from '@/stores/search'
import type { SearchTag } from '@/types/search-types'
import { cacheGet } from '@/utils/local-storage-cache'

type SearchFilterParams = {
  emit: (event: 'filter-update', ...args: any[]) => void
}

/**
 * Composable for search filters to handle tag updates and pass
 * up (via emit) up the food chain.
 */
export function useSearchFilter({ emit }: SearchFilterParams) {
  const userAuthStore = useUserAuthStore()
  const searchStore = useSearchStore()

  function triggerFiltering(tagItems: SearchTag | SearchTag[]) {
    // force array if single item
    if (!Array.isArray(tagItems)) {
      tagItems = [tagItems]
    }

    tagItems.forEach((tag) => {
      searchStore.handleTagUpdate(tag)
    })

    emit('filter-update')
  }

  /**
   * Initialize the expand / collapse state of filter groupings in pinia,
   * taking into account local storage cache.
   *
   * This allows to remember / restore the last expander state of each grouping
   */
  function initFilterGroupingsExpandedCache({
    cacheKey,
    groupingIds,
  }: {
    cacheKey: string
    groupingIds: Record<string, string>
  }): void {
    // default to collapse if no cache
    const groupingDefaults = Object.keys(groupingIds).reduce(
      (acc, key) => {
        acc[key] = false
        return acc
      },
      {} as Record<string, boolean>,
    )

    const groupingCache = cacheGet({
      key: cacheKey,
      defaultValue: groupingDefaults,
    })

    // merge defaults with cache
    const groupingsExpanded = {
      ...groupingDefaults,
      ...groupingCache,
    }

    // update pinia
    searchStore.setFilterGroupingsExpanded(groupingsExpanded)
  }

  return {
    features: computed(() => userAuthStore.features),
    checkboxFilters: computed(() => searchStore.checkboxFilters),
    rangeFilters: computed(() => searchStore.rangeFilters),
    dropdownFilters: computed(() => searchStore.dropdownFilters),
    triggerFiltering,
    initFilterGroupingsExpandedCache,
  }
}
